import { ReactElement, useState } from 'react'
import { AdPlatformCell } from 'app/components/contacts/AdPlatformCell'
import { ContactAdPlatformMappingForm } from 'app/components/contacts/ContactAdPlatformMappingForm'
import { InlineAddActionLink } from 'app/components/shared/InlineAddActionLink'
import Spinner from 'app/components/Spinner'
import type { ContactJson } from 'app/types/entities'
import { closeModal, showModal } from 'app/lib/modal'
import { notifySuccess } from 'app/services/notify.client'
import { useAdPlatformMappingStore } from 'app/stores/StoreContext'
import { useUser } from 'app/utils'
import { AdPlatformMappingType } from 'lib/adPlatformDefaults'
import { isSMCOrg } from 'lib/isSMCOrg'
import { debug, logError } from 'lib/logging'

export function AdPlatformMappingCell({ contact }: { contact?: ContactJson }): ReactElement {
  const user = useUser()
  if (!user) return <></>
  const isSMC = isSMCOrg(user.orgId)
  const adPlatformMappingStore = useAdPlatformMappingStore()
  const [adPlatform, setAdPlatform] = useState<string | undefined>(contact?.platform)
  const [saving, setSaving] = useState(false)
  const { utmSource, utmMedium } = contact || {}
  const openModal = ({ utmSource, utmMedium }: { utmSource?: string; utmMedium?: string }) => {
    showModal(
      saving ? (
        <Spinner className="absolute inset-0 m-auto" />
      ) : (
        <ContactAdPlatformMappingForm
          adPlatformMapping={
            {
              orgId: user.orgId,
              workspace: user.workspaceId,
              source: utmSource,
              medium: utmMedium,
            } as AdPlatformMappingType
          }
          handleSubmit={async (adPlatformMapping) => {
            if (!adPlatformMapping.source || !adPlatformMapping.medium || !adPlatformMapping.platform)
              return { error: `Source, medium and platform are required` }
            setSaving(true)
            try {
              const result = await adPlatformMappingStore.create({
                source: adPlatformMapping.source,
                medium: adPlatformMapping.medium,
                platform: adPlatformMapping.platform,
              })
              if (result.error) return { error: result.error.message }
              setAdPlatform(adPlatformMapping.platform)
              notifySuccess(`Change Saved. This may take some time to fully apply.`)
              closeModal()
            } catch (err: unknown) {
              const msg = err instanceof Error ? err.message : `Failed to save platform mapping`
              logError(`Failed to save platform mapping`, err)
              return { error: msg }
            } finally {
              setSaving(false)
            }
          }}
        />
      ),
      {
        title: `Add Platform Mapping`,
        isFullScreen: false,
        removePadding: true,
        useRadix: true,
      },
    )
  }

  if (isSMC) return <AdPlatformCell adPlatform={adPlatform} />
  return utmSource && utmMedium ? (
    adPlatform ? (
      <AdPlatformCell adPlatform={adPlatform} />
    ) : (
      <InlineAddActionLink
        label="+ Add Platform Mapping"
        onClick={() => {
          debug(
            [`contacts`, `platform mapping`],
            `create a new platform for contact`,
            contact?.id,
            adPlatform,
            utmSource,
            utmMedium,
          )
          openModal({
            utmSource,
            utmMedium,
          })
        }}
      />
    )
  ) : (
    <></>
  )
}
